import { writable } from 'svelte/store';
import SuperTokens from 'supertokens-web-js';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import Session from 'supertokens-web-js/recipe/session';
import { buildApiUrl } from './api.js';

const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
const importMetaApiDomain = import.meta?.env?.VITE_API_URL ?? '';
const processApiDomain = typeof process !== 'undefined' && process.env ? process.env.VITE_API_URL || '' : '';
const apiDomainConfig = importMetaApiDomain || processApiDomain || runtimeOrigin || '';

// Initialize SuperTokens
SuperTokens.init({
  appInfo: {
    appName: 'WEBDEPLOY Management System',
    apiDomain: apiDomainConfig,
    websiteDomain: import.meta.env.VITE_WEBSITE_DOMAIN || window.location.origin,
    apiBasePath: '/api/auth',
    websiteBasePath: '/auth',
  },
  recipeList: [
    EmailPassword.init(),
    Session.init(),
  ],
});

// Auth store
export const authStore = writable({
  isAuthenticated: false,
  user: null,
  loading: false,
});

/**
 * Check current authentication status
 */
export async function checkAuthStatus() {
  try {
    authStore.update(state => ({ ...state, loading: true }));

    // Check server-side session directly - this is the source of truth
    const response = await fetch(buildApiUrl('/auth/user'), {
      credentials: 'include',
    });
    if (response.ok) {
      const user = await response.json();
      authStore.set({
        isAuthenticated: true,
        user,
        loading: false,
      });
    } else {
      // If server says not authenticated, clear any client-side session
      try {
        await Session.signOut();
      } catch (signOutError) {
        console.log('No session to sign out:', signOutError);
      }

      authStore.set({
        isAuthenticated: false,
        user: null,
        loading: false,
      });
    }
  } catch (error) {
    console.error('Auth status check failed:', error);
    authStore.set({
      isAuthenticated: false,
      user: null,
      loading: false,
    });
  }
}

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function login(email, password) {
  try {
    authStore.update(state => ({ ...state, loading: true }));

    const response = await EmailPassword.signIn({
      formFields: [
        { id: 'email', value: email },
        { id: 'password', value: password }
      ]
    });

    if (response.status === 'OK') {
      await checkAuthStatus();
      return { success: true };
    } else {
      authStore.update(state => ({ ...state, loading: false }));
      return {
        success: false,
        error: response.status === 'WRONG_CREDENTIALS_ERROR'
          ? 'Email o password non corretti'
          : 'Errore durante il login'
      };
    }
  } catch (error) {
    console.error('Login failed:', error);
    authStore.update(state => ({ ...state, loading: false }));
    return {
      success: false,
      error: 'Errore di connessione'
    };
  }
}

/**
 * Renew the session with the refresh token (its cookie reaches only the
 * refresh API). False when there is nothing to refresh or the server refused.
 *
 * @returns {Promise<boolean>}
 */
export async function refreshSession() {
  try {
    return await Session.attemptRefreshingSession();
  } catch (error) {
    console.log('Session refresh failed:', error);
    return false;
  }
}

/**
 * Logout current user
 */
export async function logout() {
  try {
    authStore.update(state => ({ ...state, loading: true }));
    await Session.signOut();
    authStore.set({
      isAuthenticated: false,
      user: null,
      loading: false,
    });
  } catch (error) {
    console.error('Logout failed:', error);
    authStore.set({
      isAuthenticated: false,
      user: null,
      loading: false,
    });
  }
}
